import type { ColumnDef } from '@tanstack/react-table'
import { ClipboardCheck } from 'lucide-react'

import type {
  DataTableFilterField,
  Option,
} from '@/components/data-table/types'
import {
  NOT_GRADED,
  ratingLevelName,
} from '@/features/evaluation-rubrics/rubricTypes'
import type { CallRubricOption } from '@/hooks/useCallRubricOptions'
import { field } from '@/lib/store/schema'
import type { FilterCondition } from '@/utils/filterUtils'
import type { CallRecord } from './types'

/**
 * Call Logs filters on post-call rubric results: one sidebar filter per rubric
 * the org's calls are graded against, built from the org's rubric config.
 *
 * Each filter's id is `rubric:<title>`, which also names its URL param; the
 * condition sent to relay carries the rubric's id, which is what it matches.
 * Relay matches them (`relay/server/calls/rubric_filters.py`); the option
 * values below are its contract.
 */

const FIELD_PREFIX = 'rubric:'
export const RUBRIC_FILTER_GROUP = 'Rubrics'
/** Calls with no result for the rubric. Relay's `NOT_GRADED`. */
export { NOT_GRADED }

export function rubricFilterId(title: string): string {
  return `${FIELD_PREFIX}${title}`
}

export function isRubricFilterId(id: string): boolean {
  return id.startsWith(FIELD_PREFIX)
}

/** Whether a URL query string carries a rubric filter, e.g. a shared link. */
export function hasRubricFilterParam(search: string): boolean {
  return [...new URLSearchParams(search).keys()].some(isRubricFilterId)
}

function rubricFilterOptions(rubric: CallRubricOption): Option[] {
  const options: Option[] =
    rubric.type === 'pass_fail'
      ? [
          { label: 'Pass', value: 'pass' },
          { label: 'Fail', value: 'fail' },
        ]
      : rubric.type === 'rating'
        ? rubric.options.map((name, i) => ({
            label: ratingLevelName(i, name),
            value: String(i + 1),
          }))
        : rubric.options
            // Reserved since this shipped; a label saved before can't be
            // told apart from "Not graded", so it isn't offered.
            .filter((label) => label !== NOT_GRADED)
            .map((label) => ({ label, value: label }))
  return [...options, { label: 'Not graded', value: NOT_GRADED }]
}

export function createRubricFilterFields(
  rubrics: CallRubricOption[]
): DataTableFilterField<CallRecord>[] {
  return rubrics.map((rubric) => ({
    label: rubric.title,
    value: rubricFilterId(rubric.title) as keyof CallRecord,
    type: 'checkbox',
    compact: true,
    defaultOpen: true,
    group: RUBRIC_FILTER_GROUP,
    triggerIcon: ClipboardCheck,
    options: rubricFilterOptions(rubric),
  }))
}

// Labels may hold the `,` that joins the list, so the field's own codec
// escapes it (and `%`); the URL parser uses this codec as is.
const escapeItem = (item: string) =>
  item.replaceAll('%', '%25').replaceAll(',', '%2C')
const unescapeItem = (item: string) =>
  item.replaceAll('%2C', ',').replaceAll('%25', '%')

function rubricValuesField() {
  return field
    .array(field.string())
    .serialize((values) =>
      Array.isArray(values)
        ? values
            .filter((v): v is string => typeof v === 'string')
            .map(escapeItem)
            .join(',')
        : ''
    )
    .parse((text) =>
      text ? text.split(',').filter(Boolean).map(unescapeItem) : []
    )
    .default([])
}

/** URL params for the rubric filters, alongside `callsSchema`'s own. */
export function rubricFilterSchemaFields(rubrics: CallRubricOption[]) {
  return Object.fromEntries(
    rubrics.map((rubric) => [rubricFilterId(rubric.title), rubricValuesField()])
  )
}

/**
 * Hidden columns that hold the rubric filters' values: the sidebar's filters
 * read and write their value through a table column of the same id. They are
 * never shown; the results are in the call's side sheet.
 */
export function createRubricFilterColumns(
  rubrics: CallRubricOption[]
): ColumnDef<CallRecord>[] {
  return rubrics.map((rubric) => ({
    id: rubricFilterId(rubric.title),
    accessorFn: () => null,
    header: rubric.title,
    enableHiding: false,
    enableSorting: false,
    // An explicit array filter: TanStack's automatic one would drop the
    // selection, judging it by the accessor's (null) value.
    filterFn: 'arrSome',
    meta: { filterOnly: true },
  }))
}

/**
 * One relay condition per rubric with a selection. The value is JSON because
 * titles and labels are free text that may hold the commas the `in` filters
 * split on.
 */
export function rubricFilterConditions(
  rubrics: CallRubricOption[],
  state: Record<string, unknown>
): FilterCondition[] {
  const conditions: FilterCondition[] = []
  for (const rubric of rubrics) {
    const id = rubricFilterId(rubric.title)
    const selected = state[id]
    if (!Array.isArray(selected)) continue
    // Only values the rubric offers now: a selection made before its type or
    // options changed (or a stale shared link) would otherwise send relay a
    // value it refuses, failing the whole list.
    const offered = new Set(rubricFilterOptions(rubric).map((o) => o.value))
    const values = selected.filter(
      (v): v is string => typeof v === 'string' && offered.has(v)
    )
    if (values.length === 0) continue
    conditions.push({
      id: `${id}-in`,
      field: 'rubric',
      operator: 'in',
      value: JSON.stringify({
        // Relay matches by id when given, reaching results graded under an
        // older title; the title is the fallback for a rubric without one.
        ...(rubric.rubric_ids?.length ? { rubric_ids: rubric.rubric_ids } : {}),
        title: rubric.title,
        type: rubric.type,
        values,
        // A rating's scale, so relay matches only results graded on it, as
        // the Metrics page keeps a rescaled rating's results apart.
        ...(rubric.type === 'rating' ? { levels: rubric.options } : {}),
      }),
    })
  }
  return conditions
}
